import moment from 'moment';
import HelperMixin from '../../Shared/HelperMixin.vue';
import PaginationHelper from '../../Shared/PaginationHelper.vue';
import BranchGroupList from '../../Shared/BranchGroupList.vue';
import CollapsibleSummary from '../../Shared/CollapsibleSummary.vue';

/** سندات مخازن الوزارة (بلا مرفق) تُفتح كجهة بهذا المعرّف — نفس MinistryStoresBranchKey في الخادم. */
const MINISTRY_STORES_KEY = -1;
import { buildVoucherPrintHtml, openPrintWindow } from './printOfficialVoucher';

const TYPE_OPTIONS = [
    { value: 'issue', label: 'سند صرف' },
    { value: 'receipt', label: 'سند دخول' },
    { value: 'return', label: 'سند إرجاع' },
    { value: 'damage', label: 'سند إعدام' },
    { value: 'invoice', label: 'فاتورة نهائية' },
    { value: 'count', label: 'سند جرد' },
];

const TYPE_BADGE_CLASS = {
    issue: 'vouchers-type-badge is-issue',
    receipt: 'vouchers-type-badge is-receipt',
    return: 'vouchers-type-badge is-return',
    damage: 'vouchers-type-badge is-damage',
    invoice: 'vouchers-type-badge is-invoice',
    count: 'vouchers-type-badge is-count',
};

export default {
    name: 'OfficialVouchers',
    mixins: [HelperMixin],
    components: { PaginationHelper, BranchGroupList, CollapsibleSummary },

    async created() {
        await this.CheckLoginStatus();
        if (!this.canViewPage) {
            this.$helper.ShowMessage('error', 'غير مصرح', 'ليس لديك صلاحية للوصول إلى السندات الرسمية');
            this.href('/Home');
            return;
        }
        await this.loadBranches();
        // إدارة الصيدلة تبدأ بقائمة المستشفيات (طلب مهندس الوزارة 2026-10-05)؛
        // المرفق يرى سنداته كما كان.
        if (this.isMinistryView) {
            const q = this.$route.query || {};
            const branchId = Number(q.branchId);
            if (branchId) {
                this.viewMode = 'hospital';
                this.selectedBranch = { id: branchId, name: q.branchName || '' };
            } else {
                this.viewMode = q.view === 'all' ? 'all' : 'hospitals';
            }
        }
        this.ApplyFilters();
    },

    computed: {
        canViewPage() {
            if (!this.loginDetails) return false;
            if (this.hasInventoryAndStockAccessUi()) return true;
            if (this.isMinistryPharmacyDepartmentRole()) return true;
            if (this.isFacilityCenterRole() && this.loginDetails.branchesId) return true;
            return false;
        },
        /** الوزارة (إدارة الصيدلة ومسؤول النظام) — لا المرفق. */
        isMinistryView() {
            return !!this.loginDetails && !this.isFacilityCenterRole();
        },
        summaryTitle() {
            if (this.viewMode === 'hospital' && this.selectedBranch) return 'ملخص — ' + (this.selectedBranch.name || 'الجهة');
            return this.viewMode === 'all' ? 'ملخص — كل السندات' : 'ملخص — كل الجهات';
        },
        summaryHint() {
            const s = this.Statistics || {};
            const parts = [(s.total || 0) + ' سند'];
            if (s.returns) parts.push('إرجاع: ' + s.returns);
            if (s.damage) parts.push('إعدام: ' + s.damage);
            if (s.invoice) parts.push('فواتير: ' + s.invoice);
            if (s.lastDate) parts.push('آخر سند: ' + moment(s.lastDate).format('DD/MM/YYYY'));
            return parts.join(' · ');
        },
        branchGroupRows() {
            return (this.branchRows || []).map((r) => {
                const id = r.branchesId == null ? MINISTRY_STORES_KEY : r.branchesId;
                return {
                    key: id,
                    id,
                    name: r.branchName || '—',
                    total: r.total || 0,
                    lastDate: r.lastDate,
                    badges: [
                        { label: 'صرف', value: r.issue, cls: 'bg-label-info' },
                        { label: 'دخول', value: r.receipt, cls: 'bg-label-success' },
                        { label: 'إرجاع', value: r.returns, cls: 'bg-label-warning' },
                        { label: 'إعدام', value: r.damage, cls: 'bg-label-danger' },
                        { label: 'جرد', value: r.count, cls: 'bg-label-secondary' },
                        { label: 'فواتير', value: r.invoice, cls: 'bg-label-primary' },
                    ],
                };
            });
        },
        statsCards() {
            const s = this.Statistics || {};
            return [
                this.statCard('إجمالي السندات', s.total, 'primary', 'ti ti-files'),
                this.statCard('سندات صرف', s.issue, 'info', 'ti ti-arrow-right'),
                this.statCard('سندات دخول', s.receipt, 'success', 'ti ti-arrow-left'),
                this.statCard('سندات جرد', s.count, 'warning', 'ti ti-clipboard-list'),
            ];
        },
        typeOptions() { return TYPE_OPTIONS; },
        typeBadgeClass() {
            return (typeKey) => TYPE_BADGE_CLASS[typeKey] || 'vouchers-type-badge';
        },
    },

    data() {
        return {
            ScreenTitle: 'السندات الرسمية',
            pageNo: 1,
            pageSize: 10,
            pages: 0,
            totalCount: 0,

            // ── العرض حسب المستشفى (للوزارة) ──
            // hospitals: قائمة الجهات · hospital: سندات جهة واحدة · all: القائمة الكاملة كما كانت
            viewMode: 'all',
            selectedBranch: null,
            branchRows: [],
            branchLoading: false,
            // الأسطر المفتوحة: { [source-id]: { loading, detail } }
            openRows: {},

            FilterType: '',
            FilterFacility: '',
            FilterFrom: '',
            FilterTo: '',
            Search: '',

            Info: [],
            Statistics: {},

            branchesList: [],

            currentDetail: null,
            detailLoading: false,
        };
    },

    filters: {
        moment(d) { return d ? moment(d).format('DD/MM/YYYY') : '—'; },
        momentTime(d) { return d ? moment(d).format('DD/MM/YYYY HH:mm') : '—'; },
        toNum(n) { return n == null ? 0 : Number(n).toLocaleString('en-US'); },
        toMoney(n) {
            if (n == null) return '—';
            const v = Number(n);
            if (!Number.isFinite(v)) return '—';
            return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
    },

    methods: {
        tableRowNumber(i) {
            return ((Number(this.pageNo) - 1) * Number(this.pageSize)) + i + 1;
        },

        extractError(err) {
            if (err && err.response && err.response.data) {
                return typeof err.response.data === 'string'
                    ? err.response.data
                    : (err.response.data.message || 'حدث خطأ غير متوقع');
            }
            return (err && err.message) || 'حدث خطأ غير متوقع';
        },

        async loadBranches() {
            try {
                const res = await this.$http.GetAllBranches();
                this.branchesList = (res && res.data && res.data.info) || [];
            } catch {
                this.branchesList = [];
            }
        },

        async GetInfo(pageNo) {
            this.pageNo = pageNo || 1;
            this.$blockUI.Start();
            try {
                const res = await this.$http.GetOfficialVouchers({
                    pageNo: this.pageNo,
                    pageSize: this.pageSize,
                    Search: this.Search,
                    Type: this.FilterType,
                    BranchesId: this.effectiveBranchId(),
                    From: this.FilterFrom,
                    To: this.FilterTo,
                });
                this.Info = (res && res.data && res.data.info) || [];
                const count = (res && res.data && res.data.count) || 0;
                this.totalCount = count;
                this.pages = Math.ceil(count / this.pageSize);
            } catch (err) {
                this.Info = [];
                this.pages = 0;
                this.$helper.ShowMessage('error', 'تعذّر تحميل السندات', this.extractError(err));
            } finally {
                this.$blockUI.Stop();
            }
        },

        async GetStatistics() {
            try {
                const res = await this.$http.GetOfficialVouchersStatistics({
                    BranchesId: this.effectiveBranchId(),
                    From: this.FilterFrom,
                    To: this.FilterTo,
                });
                this.Statistics = (res && res.data) || {};
            } catch {
                this.Statistics = {};
            }
        },

        ApplyFilters() {
            this.GetStatistics();
            if (this.viewMode === 'hospitals') this.loadBranchGroups();
            else this.GetInfo(1);
        },

        /** الجهة المعروضة: المستشفى المفتوح، وإلا فلتر «الجهة» في القائمة الكاملة. */
        effectiveBranchId() {
            if (this.viewMode === 'hospital' && this.selectedBranch) return this.selectedBranch.id;
            if (this.viewMode === 'hospitals') return '';
            return this.FilterFacility;
        },

        async loadBranchGroups() {
            this.branchLoading = true;
            try {
                const res = await this.$http.GetOfficialVouchersByBranch({
                    Search: this.Search,
                    Type: this.FilterType,
                    From: this.FilterFrom,
                    To: this.FilterTo,
                });
                this.branchRows = (res && res.data && res.data.info) || [];
            } catch (err) {
                this.branchRows = [];
                this.$helper.ShowMessage('error', 'تعذّر تحميل الجهات', this.extractError(err));
            } finally {
                this.branchLoading = false;
            }
        },

        openBranch(row) {
            this.selectedBranch = { id: row.id, name: row.name };
            this.switchView('hospital');
        },

        switchView(mode) {
            this.viewMode = mode;
            if (mode !== 'hospital') this.selectedBranch = null;
            this.openRows = {};
            const query = {};
            if (mode === 'hospital' && this.selectedBranch) {
                query.branchId = String(this.selectedBranch.id);
                query.branchName = this.selectedBranch.name;
            } else if (mode === 'all') {
                query.view = 'all';
            }
            this.$router.replace({ path: this.$route.path, query }).catch(() => { });
            this.ApplyFilters();
        },

        // ── السطر المختصر يُفتح على أصنافه دون مغادرة القائمة ──
        rowKey(item) {
            return item.source + '-' + item.id;
        },
        isRowOpen(item) {
            return !!(item && this.openRows[this.rowKey(item)]);
        },
        rowState(item) {
            return this.openRows[this.rowKey(item)] || {};
        },
        async toggleRow(item) {
            const key = this.rowKey(item);
            if (this.openRows[key]) {
                this.$delete(this.openRows, key);
                return;
            }
            this.$set(this.openRows, key, { loading: true, detail: null });
            let detail = null;
            try {
                const res = await this.$http.GetOfficialVoucherOne(item.source, item.id);
                detail = (res && res.data && res.data.info) || null;
            } catch { detail = null; }
            if (this.openRows[key]) this.$set(this.openRows, key, { loading: false, detail });
        },
        lineQty(l) {
            if (l.quantity != null) return l.quantity;
            if (l.actualQty != null) return l.actualQty;
            return l.systemQty;
        },

        ResetFilters() {
            this.FilterType = '';
            this.FilterFacility = '';
            this.FilterFrom = '';
            this.FilterTo = '';
            this.Search = '';
            this.ApplyFilters();
        },

        async OpenDetail(item) {
            this.currentDetail = null;
            this.detailLoading = true;
            try {
                const res = await this.$http.GetOfficialVoucherOne(item.source, item.id);
                this.currentDetail = (res && res.data && res.data.info) || null;
            } catch (err) {
                this.$helper.ShowMessage('error', 'تعذّر تحميل التفاصيل', this.extractError(err));
            } finally {
                this.detailLoading = false;
            }
        },

        async PrintVoucher(item) {
            try {
                const res = await this.$http.GetOfficialVoucherOne(item.source, item.id);
                const data = (res && res.data && res.data.info) || null;
                if (!data) throw new Error('السند غير موجود');
                const html = buildVoucherPrintHtml(data, { mode: 'print' });
                openPrintWindow(html, false);
            } catch (err) {
                this.$helper.ShowMessage('error', 'تعذّر طباعة السند', this.extractError(err));
            }
        },

        async DownloadPdf(item) {
            try {
                const res = await this.$http.GetOfficialVoucherOne(item.source, item.id);
                const data = (res && res.data && res.data.info) || null;
                if (!data) throw new Error('السند غير موجود');
                const html = buildVoucherPrintHtml(data, { mode: 'pdf' });
                openPrintWindow(html, true);
            } catch (err) {
                this.$helper.ShowMessage('error', 'تعذّر تجهيز ملف PDF', this.extractError(err));
            }
        },

        PrintCurrentDetail() {
            if (!this.currentDetail) return;
            const html = buildVoucherPrintHtml(this.currentDetail, { mode: 'print' });
            openPrintWindow(html, false);
        },

        DownloadCurrentDetailPdf() {
            if (!this.currentDetail) return;
            const html = buildVoucherPrintHtml(this.currentDetail, { mode: 'pdf' });
            openPrintWindow(html, true);
        },
    },
};
